import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card, CardBody, CardHeader, Row, Col, Button, Badge, Table,
  Spinner, Input, Label,
} from "reactstrap";
import Select from "react-select";
import AccountingReportService from "../../../services/AccountingServices/AccountingReportService";
import FiscalYearService from "../../../services/AccountingServices/FiscalYearService";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import toast from "react-hot-toast";

const TrialBalance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [reportData, setReportData] = useState(null);

  const [filters, setFilters] = useState({
    fiscalYearCode: null,
    to: "",
  });

  useEffect(() => {
    fetchFiscalYears();
  }, []);

  const fetchFiscalYears = async () => {
    try {
      const response = await FiscalYearService.getList();
      const items = Array.isArray(response) ? response : response?.items || [];
      setFiscalYears(items.map((fy) => ({ value: fy.code, label: fy.name })));
    } catch (error) {
      console.error("Error fetching fiscal years:", error);
    }
  };

  const handleGenerate = async () => {
    if (!filters.fiscalYearCode) {
      toast.error("Please select a fiscal year");
      return;
    }
    setLoading(true);
    try {
      const params = {
        fiscalYearCode: filters.fiscalYearCode,
        to: filters.to || undefined,
      };
      const data = await AccountingReportService.getTrialBalance(params);
      setReportData(data);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch trial balance");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) =>
    (val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    control: (base) => ({
      ...base,
      minHeight: "38px",
      borderRadius: "8px",
      borderColor: "#dee2e6",
      fontSize: "0.85rem",
      "&:hover": { borderColor: "#86b7fe" },
    }),
    option: (base) => ({ ...base, fontSize: "0.85rem" }),
  };

  const getCategoryColor = (type) => {
    const colors = {
      "Cash": "success",
      "Bank": "info",
      "Income": "primary",
      "Expense": "warning",
      "Assets": "success",
      "Liabilities": "danger",
      "Equity": "dark",
    };
    return colors[type] || "secondary";
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Trial Balance" pageTitle="Accounting"/>
        {/* Page Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <div className="d-flex align-items-center gap-3">
              <div
                className="d-flex align-items-center justify-content-center"
                style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: "linear-gradient(135deg, #0ab39c 0%, #22d3ee 100%)",
                  boxShadow: "0 4px 14px rgba(10,179,156,0.3)",
                }}
              >
                <i className="ri-scales-3-line fs-22 text-white"></i>
              </div>
              <div>
                <h4 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>Trial Balance</h4>
                <p className="text-muted mb-0 small">Summary of all ledger balances for the selected fiscal period</p>
              </div>
            </div>
          </Col>
          {/* <Col xs="auto">
            <Button
              color="soft-secondary"
              size="sm"
              onClick={() => navigate(-1)}
              className="d-flex align-items-center gap-1"
            >
              <i className="ri-arrow-left-line"></i> Back
            </Button>
          </Col> */}
        </Row>

        {/* Filters Card */}
        <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: 12, overflow: "hidden" }}>
          <CardHeader
            className="border-0 py-3"
            style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
          >
            <div className="d-flex align-items-center gap-2">
              <i className="ri-filter-3-line text-primary fs-16"></i>
              <span className="fw-semibold text-dark">Report Filters</span>
            </div>
          </CardHeader>
          <CardBody className="p-3">
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Label className="form-label small fw-semibold text-muted mb-1">Fiscal Year *</Label>
                <Select
                  options={fiscalYears}
                  value={fiscalYears.find((fy) => fy.value === filters.fiscalYearCode)}
                  onChange={(opt) => setFilters((f) => ({ ...f, fiscalYearCode: opt?.value || null }))}
                  placeholder="Select Fiscal Year..."
                  isClearable
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                />
              </Col>
              <Col md={3}>
                <Label className="form-label small fw-semibold text-muted mb-1">As of Date</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={filters.to}
                  onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                  style={{ borderRadius: 8, height: 38, fontSize: "0.85rem" }}
                />
              </Col>
              <Col md={3}>
                <Button
                  color="primary"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="w-100 d-flex align-items-center justify-content-center gap-2"
                  style={{
                    height: 38, borderRadius: 8, fontWeight: 600,
                    background: "linear-gradient(135deg, #0ab39c 0%, #22d3ee 100%)",
                    border: "none",
                  }}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <i className="ri-file-search-line"></i> Generate Report
                    </>
                  )}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Summary Cards */}
        {reportData && (
          <>
            <Row className="mb-4 g-3">
              <Col md={4} sm={6}>
                <Card
                  className="shadow-sm border-0 h-100"
                  style={{
                    borderRadius: 12,
                    background: "rgba(10,179,156,0.06)",
                    borderLeft: "4px solid #0ab39c",
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                          Total Debit
                        </p>
                        <h4 className="mb-0 fw-bold" style={{ color: "#0ab39c" }}>
                          {formatCurrency(reportData.totalDr)}
                        </h4>
                      </div>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: 48, height: 48, borderRadius: 12,
                          background: "rgba(10,179,156,0.12)",
                        }}
                      >
                        <i className="ri-arrow-up-circle-line fs-24" style={{ color: "#0ab39c" }}></i>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4} sm={6}>
                <Card
                  className="shadow-sm border-0 h-100"
                  style={{
                    borderRadius: 12,
                    background: "rgba(240,101,72,0.06)",
                    borderLeft: "4px solid #f06548",
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                          Total Credit
                        </p>
                        <h4 className="mb-0 fw-bold" style={{ color: "#f06548" }}>
                          {formatCurrency(reportData.totalCr)}
                        </h4>
                      </div>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: 48, height: 48, borderRadius: 12,
                          background: "rgba(240,101,72,0.12)",
                        }}
                      >
                        <i className="ri-arrow-down-circle-line fs-24" style={{ color: "#f06548" }}></i>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md={4} sm={12}>
                <Card
                  className="shadow-sm border-0 h-100"
                  style={{
                    borderRadius: 12,
                    background: reportData.totalDr === reportData.totalCr
                      ? "rgba(10,179,156,0.06)"
                      : "rgba(240,101,72,0.06)",
                    borderLeft: `4px solid ${reportData.totalDr === reportData.totalCr ? "#0ab39c" : "#f06548"}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 25px rgba(0,0,0,0.08)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "";
                  }}
                >
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p className="text-muted small fw-semibold mb-1 text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>
                          Difference
                        </p>
                        <h4 className="mb-1 fw-bold" style={{ color: reportData.totalDr === reportData.totalCr ? "#0ab39c" : "#f06548" }}>
                          {formatCurrency(Math.abs((reportData.totalDr || 0) - (reportData.totalCr || 0)))}
                        </h4>
                        {reportData.totalDr === reportData.totalCr ? (
                          <Badge color="success" className="px-2 py-1 fs-10" style={{ borderRadius: 6 }}>
                            <i className="ri-check-line me-1"></i>Balanced
                          </Badge>
                        ) : (
                          <Badge color="danger" className="px-2 py-1 fs-10" style={{ borderRadius: 6 }}>
                            <i className="ri-alert-line me-1"></i>Unbalanced
                          </Badge>
                        )}
                      </div>
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: 48, height: 48, borderRadius: 12,
                          background: reportData.totalDr === reportData.totalCr
                            ? "rgba(10,179,156,0.12)"
                            : "rgba(240,101,72,0.12)",
                        }}
                      >
                        <i
                          className={`ri-scales-3-line fs-24`}
                          style={{ color: reportData.totalDr === reportData.totalCr ? "#0ab39c" : "#f06548" }}
                        ></i>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Trial Balance Table */}
            <Card className="shadow-sm border-0" style={{ borderRadius: 12, overflow: "hidden" }}>
              <CardHeader
                className="border-0 py-3"
                style={{ background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}
              >
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <i className="ri-list-check-2 text-primary fs-16"></i>
                    <span className="fw-semibold text-dark">Ledger Balances</span>
                  </div>
                  <Badge color="soft-primary" className="px-3 py-2 fs-12" style={{ borderRadius: 8 }}>
                    {reportData.ledgers?.length || 0} Ledgers
                  </Badge>
                </div>
              </CardHeader>
              <div className="table-responsive">
                <Table className="table-hover align-middle mb-0" style={{ fontSize: "0.85rem" }}>
                  <thead>
                    <tr style={{ background: "#f8fafc" }}>
                      <th className="text-muted fw-semibold small text-uppercase" style={{ width: 50, letterSpacing: "0.5px" }}>#</th>
                      <th className="text-muted fw-semibold small text-uppercase" style={{ width: 90, letterSpacing: "0.5px" }}>Code</th>
                      <th className="text-muted fw-semibold small text-uppercase" style={{ letterSpacing: "0.5px" }}>Ledger Name</th>
                      <th className="text-muted fw-semibold small text-uppercase text-center" style={{ width: 110, letterSpacing: "0.5px" }}>Type</th>
                      <th className="text-muted fw-semibold small text-uppercase text-end" style={{ width: 140, letterSpacing: "0.5px" }}>Debit</th>
                      <th className="text-muted fw-semibold small text-uppercase text-end" style={{ width: 140, letterSpacing: "0.5px" }}>Credit</th>
                      <th className="text-muted fw-semibold small text-uppercase text-end" style={{ width: 140, letterSpacing: "0.5px" }}>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.ledgers && reportData.ledgers.length > 0 ? (
                      reportData.ledgers.map((ledger, idx) => (
                        <tr
                          key={ledger.ledgerId}
                          style={{ cursor: "pointer", transition: "background 0.15s" }}
                          onClick={() => navigate(`/ledger-statement?ledgerId=${ledger.ledgerId}`)}
                        >
                          <td className="text-muted">{idx + 1}</td>
                          <td>
                            <span
                              className="fw-medium px-2 py-1 d-inline-block"
                              style={{
                                background: "rgba(64,81,137,0.06)",
                                borderRadius: 6,
                                fontSize: "0.82rem",
                                color: "#405189",
                              }}
                            >
                              {ledger.ledgerCode}
                            </span>
                          </td>
                          <td>
                            <span className="fw-semibold text-dark">{ledger.ledgerName}</span>
                          </td>
                          <td className="text-center">
                            <Badge
                              color={getCategoryColor(ledger.ledgerType)}
                              className="text-uppercase px-2 py-1 fs-10"
                              style={{ minWidth: 65, borderRadius: 6 }}
                            >
                              {ledger.ledgerType}
                            </Badge>
                          </td>
                          <td className="text-end">
                            {ledger.totalDr > 0 ? (
                              <span className="fw-semibold" style={{ color: "#0ab39c" }}>
                                {formatCurrency(ledger.totalDr)}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-end">
                            {ledger.totalCr > 0 ? (
                              <span className="fw-semibold" style={{ color: "#f06548" }}>
                                {formatCurrency(ledger.totalCr)}
                              </span>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td className="text-end">
                            <span
                              className="fw-bold"
                              style={{
                                color: ledger.balance > 0 ? "#0ab39c" : ledger.balance < 0 ? "#f06548" : "#405189",
                              }}
                            >
                              {formatCurrency(ledger.balance)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center py-5">
                          <div className="text-muted">
                            <i className="ri-scales-3-line fs-36 d-block mb-2 opacity-25"></i>
                            <p className="mb-0">No ledger data found</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {reportData.ledgers && reportData.ledgers.length > 0 && (
                    <tfoot>
                      <tr style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #f8fafc 100%)", fontWeight: 700 }}>
                        <td colSpan={4} className="text-end fw-bold text-dark pe-3">
                          TOTALS
                        </td>
                        <td className="text-end fw-bold" style={{ color: "#0ab39c", fontSize: "0.9rem" }}>
                          {formatCurrency(reportData.totalDr)}
                        </td>
                        <td className="text-end fw-bold" style={{ color: "#f06548", fontSize: "0.9rem" }}>
                          {formatCurrency(reportData.totalCr)}
                        </td>
                        <td className="text-end fw-bold" style={{ color: "#405189", fontSize: "0.9rem" }}>
                          {formatCurrency(Math.abs((reportData.totalDr || 0) - (reportData.totalCr || 0)))}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            </Card>
          </>
        )}

        {/* Empty State */}
        {!reportData && !loading && (
          <Card className="shadow-sm border-0 text-center py-5" style={{ borderRadius: 12 }}>
            <CardBody>
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 80, height: 80, borderRadius: "50%",
                  background: "linear-gradient(135deg, rgba(10,179,156,0.1) 0%, rgba(34,211,238,0.1) 100%)",
                }}
              >
                <i className="ri-scales-3-line fs-36" style={{ color: "#0ab39c" }}></i>
              </div>
              <h5 className="fw-bold text-dark mb-2">Generate Trial Balance</h5>
              <p className="text-muted mb-0" style={{ maxWidth: 400, margin: "0 auto" }}>
                Select a fiscal year and optional date above, then click <strong>Generate Report</strong> to view the trial balance summary.
              </p>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};

export default TrialBalance;
